import { Injectable } from '@angular/core';
import { HttpHandler, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Utils } from '../../../_helpers/utilities';
import { LoadingController } from '../../modules/loader/loader-controller.service';
import { SocketService } from '../socket/socket.service';
import { applicationUrls } from 'sharedweb/helpers/urls';
import { SocketErrorCodes } from 'sharedweb/app.structs';
import { parseErrorMessageWeb } from 'sharedweb/providers/shared-message/shared.message';
import { REST_BUILD } from 'src/app/_helpers/configuration';
import { ERRORCODES } from 'sharedweb/providers/socket/socket.constants';
import { AuthService, EventsService } from '..';
import { EVENTS } from 'src/app/_helpers';
import { ConfigurationService } from '../configuration/configuration.service';
import * as CryptoJS from 'crypto-js';
import { ActivatedRoute } from '@angular/router';

@Injectable()
export class HttpInterceptorService {

    private requestCount = 0;
    private storageHelper = Utils.storageHelper;
    private showingLoader = false;

    constructor(
        private _toastr: ToastrService,
        private _loader: LoadingController,
        private _socket: SocketService,
        private _eventService: EventsService,
        private remoteConfigService: ConfigurationService,
        private activatedRoute: ActivatedRoute
    ) { }

    errorHandler = catchError((error, caught) => {

        let errorMsg = null;

        if (error.url !== applicationUrls.geolocationAPIUrl) {
            this._eventService.publish(EVENTS.CHECK_INTERNET);
        } else {
            return throwError(error);
        }

        if (error.status === 401 && document.hidden) {
            console.log("Tab is hidden, skipping logging out");
        }

        // Skipping logging out user in case tab is inactive as we dont want to send persistent token otp in that case. Refer RE-913.
        // When tab becomes active again, user will be logged out automatically in case of any activity from user or socket connection unauthorization.
        if (error.status === 401 && !document.hidden) {
            this._socket.disconnectAllSockets();
            // RT-11451 Not showing error message of logged out in case of reset_password url as custom one is only needed.
            if (error.error && !window.location.href.includes('reset_password')) {
                if(!REST_BUILD){
                    errorMsg = '';
                } else {
                    errorMsg = error.error.error;
                }
            } else {
                errorMsg = 'Invalid Authorization';
            }
            if (errorMsg) {
                // Page is reloaded so error msg will be not shown in default flow.
                this.showToast(errorMsg);
                this.storageHelper.clear();
            } else if(this.storageHelper.userInfo && this.storageHelper.userInfo.access_token){
                const userInfo = this.storageHelper.userInfo;
                userInfo.access_token = '';
                this.storageHelper.userInfo = userInfo;
            }
            console.log('Invalid Authorization');
            // Ask user to Re-login
            // localStorage.removeItem(LocalStorageKeys.userInfo);

            setTimeout(() => {
                // Giving 1sec for toast to be read then reload/
                // RT-11451 Not reloading page in case of reset_password url as it is not needed will effect user experience.
                if (!window.location.href.includes('reset_password') && !window.location.href.includes('biometric') && !REST_BUILD) {
                    location.reload();
                }
            }, 1000);
        } else if (error.status === 500) {
            errorMsg = 'Server Error! Something\'s wrong.';
            console.log('Server Error! Something\'s wrong.');
        } else {
            const err = error.error;
            // RE-1734 Showing custom error messages for user status codes from remote config.
            const errorMessages = this.remoteConfigService.remoteConfigCacheObj.userStatusMessages;
            if(err && errorMessages &&errorMessages[err.code]){ 
                errorMsg = errorMessages[err.code];
                this.showCustomToast(errorMsg);
                return throwError(error);
            }
            if (err && err.hasOwnProperty('error') && Array.isArray(err.error)) {
                err.error.forEach(e => {
                    this.showToast(e);
                });
            }
            /**
             * if Recieved Error Code 1015 (means web server didn't find any active session with session_key)
             * so logging user out
            */
            if (err?.code == 1015) {
                this.storageHelper.clear();
                setTimeout(() => {
                    // Giving 1sec for toast to be read then reload/
                    location.reload();
                }, 1000);
            }
            if (err && err.hasOwnProperty('error') && typeof err.error === 'string') {
                errorMsg = err.error;
            } else if (err && err.hasOwnProperty('status') && err.status === 'error') {
                errorMsg = err.message;
            } else {
                // RT-10098 generic message for all error status except 401 and 500
                errorMsg = 'Connectivity Error! Please try again after ensuring Internet Connectivity.';
                console.log('Connectivity Error! Please try again after ensuring Internet Connectivity.');

            }
            // else if (typeof err === 'object') {
            //     errorMsg = err[Object.keys(err)[0]][0];
            // }
        }
        // RE-4321 Not to show any toast for quick links. As in such case, need to redirect to the fallback URL.
        if (errorMsg && (error.error.code === SocketErrorCodes.UserDoesNotExist || error.error.code === SocketErrorCodes.ExchangeNotMapped)) {
            const errorMessage = parseErrorMessageWeb(error.error.code, errorMsg)
            this.showCustomToast(errorMessage);
        } else if (errorMsg && (!ERRORCODES.includes(error.error.code)) && error.url !== applicationUrls.getQuickLinks) { 
            this.showToast(errorMsg);
        }

        // Hide Loader
        if (this.showingLoader) {
            this.requestCount -= 1;

            if (this.requestCount === 0) {
                this._loader.hide();
                this.showingLoader = false;
            }
        }

        // return the error to the method that called it
        return throwError(error);
    }) as any;

    successHandler = map<any, any>(res => {
        if (res.hasOwnProperty('body')) {
            // Hide Loader
            if (this.showingLoader) {
                this.requestCount -= 1;

                if (this.requestCount === 0) {
                    this._loader.hide();
                    this.showingLoader = false;
                }
            }
        }
        return res;
    });

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (request.url !== applicationUrls.geolocationAPIUrl) { 
            // Show Loader
            if (request.url.indexOf('accounts') >= 0 && (request.url.indexOf('grant')<0 || request.url.indexOf('pesb') >= 0)) {
                this.requestCount += 1;
                this.showingLoader = true;
                this._loader.show();
            }
            if (!request.url.endsWith(applicationUrls.loginUrl)) {
                request = this.addAuthorizationHeader(request, this.storageHelper?.userInfo?.access_token);
            } 
            if (request.url.includes(applicationUrls?.analyticsServerUrl)) {
                const apiKey = this.remoteConfigService.remoteConfigCacheObj?.ADVISOR_SERVER_CONFIG?.api_key;
                const method = request.method;
                const path = new URL(request.url).pathname;
                const bodyStr = request.body ? JSON.stringify(request.body) : '';
                const { signature, timestamp } = this.generateRequestSignature(method, path, bodyStr);
                request = request.clone({
                    setHeaders: {
                        'X-API-Key': apiKey,
                        'X-Signature': signature,
                        'X-Timestamp': timestamp,
                    }
                });
            }
        }
        return next.handle(request).pipe(this.successHandler, this.errorHandler) as Observable<HttpEvent<any>>;
    }

    showToast(errorMsg: string) {
        // Show error only once if they have the same message, other toasts can be shown again and again.
        this._toastr.toastrConfig.preventDuplicates = true;
        this._toastr.error(errorMsg, 'Error');
        this._toastr.toastrConfig.preventDuplicates = false;
    }

    private addAuthorizationHeader(request, access_token?: string) {
        const storageHelper = Utils.storageHelper;
        const newHeaders = {};
        if (access_token) {
            newHeaders['Authorization'] = storageHelper.userInfo.tokenType + ' ' + access_token;
        } else if (storageHelper.userInfo && storageHelper.userInfo.access_token && storageHelper.userInfo.tokenType) {
            newHeaders['Authorization'] = storageHelper.userInfo.tokenType + ' ' + storageHelper.userInfo.access_token;
        } else if (this.activatedRoute.snapshot.queryParams.consent_id && request.url.includes("otp_verify")) {
        //consent id is used to track the consent id for the user in algos server that he has given consent to the broker to login using shareindia server
            newHeaders['consent-id'] = this.activatedRoute.snapshot.queryParams.consent_id;
        }

        request = request.clone({
            setHeaders: newHeaders
        });
        return request;
    }

    private generateRequestSignature(method: string, path: string, body: string = ''): { signature: string; timestamp: string } {
        const timestamp = Math.floor(Date.now() / 1000).toString();
        const secretKey = this.remoteConfigService.remoteConfigCacheObj?.ADVISOR_SERVER_CONFIG?.secret_key;
        const message = `${timestamp}${method}${path}${body}`;
        const signature = CryptoJS.HmacSHA256(message, secretKey).toString();
        return { signature, timestamp };
    }


    showCustomToast(customMsg: string) {
        // Show error only once if they have the same message, other toasts can be shown again and again.
        this._toastr.toastrConfig.preventDuplicates = true;

        // Find and replace the link in the error message with an anchor tag
        const linkPattern = /(https?:\/\/[^\s]+)/;
        const replacedMsg = customMsg.replace(linkPattern, '<a href="$&" target="_blank">$&</a>');

        // Create a custom HTML template for the toast
        const toastTemplate = `
            <div>
                <p>${replacedMsg}</p>
            </div>
        `;

        // Show the error toast with the custom template
        this._toastr.warning(toastTemplate, 'Error', {
            enableHtml: true,
            closeButton: true,
            tapToDismiss: false,
            timeOut: 0,
            extendedTimeOut: 0,
        });

        this._toastr.toastrConfig.preventDuplicates = false;
    }
}
